/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosButtonFragment } from "@cosmos/hygraph-schema";

import { Animate, CosmosButton, CosmosButtonProps, IconSize, Wrap } from "@components/elements";
import { cx } from "@panda/css";
import { HStack, HstackProps } from "@panda/jsx";
import { ButtonProps } from "@eds-web/react";

export interface ButtonListProps {
	animate?: boolean;
	buttons?: CosmosButtonProps[] | CosmosButtonFragment[] | null;
	size?: ButtonProps["size"] | null;
	iconSize?: IconSize;
	className?: string;
	stack?: HstackProps;
}

function isReactElementProps(button: CosmosButtonProps | CosmosButtonFragment): button is CosmosButtonProps {
	return (button as CosmosButtonProps).children !== undefined;
}

export const ButtonList = ({
	animate = false,
	buttons,
	size = "md",
	iconSize,
	className,
	stack = {},
}: ButtonListProps): React.JSX.Element | null => {
	if (!buttons || !buttons.length) {
		return null;
	}

	const valid = buttons.some((b) => !!b.label);
	if (!valid) return null;

	const Buttons = buttons.map((button, i) => {
		if (!button.label) return null;

		let children: React.ReactNode | string = button.label;
		if (isReactElementProps(button) && button.children) {
			children = button.children;
		}

		return (
			<Wrap
				condition={animate}
				key={button.label || `cosmos-button-${i}`}
				wrap={(children) => (
					<Animate delay={0.35 + 0.1 * i} type="slide" distance="1rem">
						{children}
					</Animate>
				)}
			>
				<CosmosButton size={size} iconSize={iconSize || "md"} {...button}>
					{children}
				</CosmosButton>
			</Wrap>
		);
	});

	return (
		<HStack
			className={cx(`button-list`, className)}
			gap={size === "lg" || size === "xl" ? 6 : 4}
			flexShrink={0}
			flexWrap="wrap"
			{...stack}
		>
			{Buttons}
		</HStack>
	);
};
